/**
 * A dropdown you can also type into.
 *
 * Built on the native `<datalist>` rather than a custom listbox, and that is a
 * deliberate choice rather than a lazy one. It is searchable as you type, it is
 * keyboard- and screen-reader-accessible for free, it works on a phone browser
 * in a workshop, and — the part that matters here — **anything you type that is
 * not on the list is still accepted**.
 *
 * That last property is the whole requirement. A shop's part names, marques and
 * body styles are theirs; a closed dropdown would tell an estimator their own
 * vocabulary is wrong. The list is a shortcut for the common ninety per cent,
 * never a fence.
 */

import { useId } from 'react';

export function Combo({ label, value, onChange, options, placeholder, hint, required }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Suggestions. Typing something else is always allowed. */
  options: readonly string[];
  placeholder?: string;
  hint?: string;
  required?: boolean;
}) {
  const listId = useId();
  return (
    <label className="field">
      <span>{label}{hint && <em className="hint"> — {hint}</em>}</span>
      <input
        list={listId}
        value={value}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </label>
  );
}

/** The five the vehicle record uses. Blank is a real, useful answer. */
export const BODY_STYLES = ['', 'sedan', 'suv', 'pickup', 'van', 'other'] as const;

export const PART_TYPE_OPTIONS = [
  'new_oem', 'aftermarket', 'used', 'existing', 'sublet', 'none',
] as const;

/** Marques a Canadian shop actually sees. Type anything else. */
export const MAKES = [
  'Toyota', 'Honda', 'Ford', 'Chevrolet', 'GMC', 'Ram', 'Dodge', 'Jeep',
  'Nissan', 'Hyundai', 'Kia', 'Mazda', 'Subaru', 'Volkswagen', 'BMW',
  'Mercedes-Benz', 'Audi', 'Lexus', 'Acura', 'Infiniti', 'Volvo', 'Tesla',
  'Mitsubishi', 'Buick', 'Cadillac', 'Chrysler', 'Lincoln', 'Land Rover',
  'Porsche', 'MINI', 'Genesis',
] as const;

/**
 * Panel names in the words an estimator uses.
 *
 * Ordered roughly front-to-back rather than alphabetically, because that is how
 * somebody walks a car — and matching the AI's phrasing here is what makes a
 * row get found later, so the list doubles as a nudge toward names that work.
 */
export const PART_NAMES = [
  'front bumper cover', 'front bumper reinforcement bar', 'front bumper absorber',
  'grille, upper', 'grille, lower', 'hood', 'hood hinge',
  'headlamp assembly LH', 'headlamp assembly RH', 'fog lamp LH', 'fog lamp RH',
  'front fender LH', 'front fender RH', 'fender liner LH', 'fender liner RH',
  'radiator support', 'radiator', 'condenser', 'cooling fan assembly',
  'windshield', 'cowl panel',
  'front door shell LH', 'front door shell RH',
  'rear door shell LH', 'rear door shell RH',
  'door mirror LH', 'door mirror RH',
  'rocker panel LH', 'rocker panel RH',
  'quarter panel LH', 'quarter panel RH',
  'roof panel', 'B-pillar LH', 'B-pillar RH',
  'tailgate', 'liftgate', 'trunk lid', 'rear body panel',
  'rear bumper cover', 'rear bumper reinforcement bar',
  'tail lamp LH', 'tail lamp RH', 'backup lamp',
  'wheel', 'tyre', 'wheel arch moulding',
  'curtain airbag LH', 'curtain airbag RH', 'seat belt pretensioner',
  'wheel alignment', 'ADAS calibration', 'clip set',
] as const;
